/**
 * Onboarding Screen 4: Focus Preview
 * Show how a Focus works with animated plan items
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, CheckCircle2, Circle, Lightbulb, Calendar, ArrowRight } from 'lucide-react';
import { sampleFocusData } from '../data/sampleData';

interface FocusPreviewProps {
  onNext: () => void;
}

export const FocusPreview: React.FC<FocusPreviewProps> = ({ onNext }) => {
  const [visibleItems, setVisibleItems] = useState<number>(0);
  const [showInsights, setShowInsights] = useState(false);

  useEffect(() => {
    // Animate plan items appearing one by one
    const timers: NodeJS.Timeout[] = [];
    
    sampleFocusData.planItems.forEach((_, index) => {
      const timer = setTimeout(() => {
        setVisibleItems(index + 1);
      }, 800 + index * 600); // Start after 800ms, then 600ms between each
      timers.push(timer);
    });

    // Show insights after all items are visible
    const insightsTimer = setTimeout(() => {
      setShowInsights(true);
    }, 800 + sampleFocusData.planItems.length * 600 + 500);
    timers.push(insightsTimer);

    return () => timers.forEach(timer => clearTimeout(timer));
  }, []);

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-8 overflow-y-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 text-center"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm mb-4">
          <Target className="w-5 h-5 text-indigo-500" />
          <span className="font-semibold text-gray-900">Focus</span>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Let Swirl think for you
        </h2>
        <p className="text-gray-600">
          Create a Focus and Swirl builds a plan, gathers insights, and keeps you on track
        </p>
      </motion.div>

      {/* Focus Card */}
      <div className="flex-1 max-w-3xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
        >
          {/* Focus Header */}
          <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 p-6 text-white">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Target className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium opacity-90">Active Focus</p>
                <h3 className="text-2xl font-bold">{sampleFocusData.title}</h3>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{sampleFocusData.timeline}</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                <span>0/{sampleFocusData.planItems.length} steps</span>
              </div>
            </div>
          </div>

          {/* Plan Items */}
          <div className="p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span>Action Plan</span>
              {visibleItems < sampleFocusData.planItems.length && (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full"
                />
              )}
            </h4>
            
            <div className="space-y-3">
              <AnimatePresence>
                {sampleFocusData.planItems.map((item, index) => (
                  index < visibleItems && (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="flex items-start gap-3 p-4 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <Circle className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-gray-900 font-medium">{item.text}</p>
                      </div>
                      <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full">
                        Step {item.id}
                      </span>
                    </motion.div>
                  )
                ))}
              </AnimatePresence>
            </div>

            {/* Insights Section */}
            <AnimatePresence>
              {showInsights && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="mt-6 pt-6 border-t border-gray-200"
                >
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-500" />
                    AI Insights
                  </h4>
                  <div className="space-y-2">
                    {sampleFocusData.insights.map((insight, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start gap-2 text-sm text-gray-700 bg-amber-50 p-3 rounded-lg border border-amber-100"
                      >
                        <ArrowRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <span>{insight}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>

      {/* Next Button */}
      <AnimatePresence>
        {showInsights && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-8 flex justify-center"
          >
            <button
              onClick={onNext}
              className="px-8 py-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all hover:scale-105"
            >
              Next
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};





